import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { useServerContext } from "src/hooks/redux";
import { formatPriceWithCurrencySymbol } from "src/utils/priceUtils";
import { CoreReduxState } from "../store";
import { ServerContextState } from "./serverContext";

export type CommitmentContractState = {
  cancellationFeeProcessingState?: "FAILED" | "COMPLETED";
};

const commitmentContractSlice = createSlice({
  name: "commitmentContract",
  initialState: {} as CommitmentContractState,
  reducers: {
    updateCommitmentContractProcessing: (
      state,
      action: PayloadAction<"FAILED" | "COMPLETED">
    ) => {
      return {
        ...state,
        cancellationFeeProcessingState: action.payload,
      };
    },
  },
});

export const { updateCommitmentContractProcessing } =
  commitmentContractSlice.actions;

export default commitmentContractSlice;

export function useCommitmentContract() {
  return useSelector((state: CoreReduxState) => state.commitmentContract);
}

export type CommitmentContractFromServerContext = {
  acceptedCommitmentContract?: boolean;
  acceptedContractTimestamp?: Date;
  contractFeeCurrencyType?: string;
  acceptedContractFee?: number;
  acceptedContractFeeDecimalPlaces?: number;
  contractExpiredTimestamp?: Date;
  originalSubscriptionId?: string;
  contractResolutionState?: "UNKNOWN" | "CHARGED" | "WAIVED" | "REFUNDED";
};

export function getCommitmentContractFromServerContext(
  serverContext: ServerContextState
): CommitmentContractFromServerContext & {
  contractFee?: number;
  formattedContractFee?: string;
  isUnderContract?: boolean;
} {
  const { commitment_contract: commitmentContract } = serverContext;

  function userIsUnderContract() {
    const now = new Date();

    return (
      !!commitmentContract &&
      commitmentContract.acceptedCommitmentContract &&
      !!commitmentContract.contractExpiredTimestamp &&
      new Date(commitmentContract.contractExpiredTimestamp) > now &&
      commitmentContract.acceptedContractFee > 0 &&
      (!commitmentContract.contractResolutionState ||
        commitmentContract.contractResolutionState === "UNKNOWN")
    );
  }

  const isUnderContract = userIsUnderContract();

  function getContractFee() {
    if (!isUnderContract) {
      return null;
    }
    const contractFee =
      (commitmentContract.acceptedContractFee || 0) /
      10 ** (commitmentContract.acceptedContractFeeDecimalPlaces || 2);
    const formattedContractFee = formatPriceWithCurrencySymbol(contractFee, {
      currency: serverContext.currency,
      currency_symbol: serverContext.currency_symbol,
    });
    return {
      contractFee,
      formattedContractFee,
    };
  }

  return {
    ...commitmentContract,
    isUnderContract,
    ...getContractFee(),
  };
}

export function useCommitmentContractFromServerContext() {
  const serverContext = useServerContext();
  return getCommitmentContractFromServerContext(serverContext);
}
